import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cx } from '../cx';
import { ArrowRight } from './icons';
import ui from '../ui.module.css';
import s from './LegalDoc.module.css';

export interface LegalSection {
  id: string;
  title: string;
  /** Plain-English summary set in the margin next to the section. */
  summary: string;
  body: ReactNode;
}

interface LegalDocProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  lastUpdated: string;
  /** Where questions about this document should go. */
  contactEmail: string;
  sections: LegalSection[];
  /** The other document, linked at the foot. */
  siblingLabel: string;
  siblingTo: string;
}

/**
 * The legal pages, set as a document rather than as an app screen: a masthead,
 * a numbered contents list, and a margin column carrying the plain-English
 * summary next to every section. Substance untouched, furniture rebuilt.
 */
export function LegalDoc({
  eyebrow,
  title,
  subtitle,
  lastUpdated,
  contactEmail,
  sections,
  siblingLabel,
  siblingTo,
}: LegalDocProps) {
  return (
    <div className={s.doc}>
      <header className={s.head}>
        <div className={cx(ui.container, s.headInner)}>
          <span className={ui.crop} aria-hidden="true" />
          <p className={ui.eyebrow}>{eyebrow}</p>
          <h1 className={s.title}>{title}</h1>
          <p className={s.subtitle}>{subtitle}</p>
          <dl className={s.meta}>
            <div>
              <dt>Last updated</dt>
              <dd>{lastUpdated}</dd>
            </div>
            <div>
              <dt>Sections</dt>
              <dd>{sections.length}</dd>
            </div>
            <div>
              <dt>Questions to</dt>
              <dd>
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
              </dd>
            </div>
          </dl>

          <p className={s.draft}>
            <b>Draft.</b> This document describes how RackSavant actually works, in language a
            person can read. It has not been through legal review, and counsel will check it before
            launch. Where the reviewed version differs from this one, the reviewed version governs.
          </p>
        </div>
      </header>

      <div className={cx(ui.container, s.layout)}>
        <nav className={s.toc} aria-label="Contents">
          <h2 className={s.tocTitle}>Contents</h2>
          <ol className={s.tocList}>
            {sections.map((section, i) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>
                  <span aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className={s.sections}>
          {sections.map((section, i) => (
            <section
              key={section.id}
              id={section.id}
              className={s.section}
              aria-labelledby={`${section.id}-title`}
            >
              <p className={s.sectionIndex} aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </p>
              <h2 id={`${section.id}-title`} className={s.sectionTitle}>
                {section.title}
              </h2>

              <div className={s.sectionGrid}>
                <aside className={s.summary}>
                  <span className={s.summaryLabel}>In short</span>
                  <p className={s.summaryText}>{section.summary}</p>
                </aside>
                <div className={s.prose}>{section.body}</div>
              </div>
            </section>
          ))}

          <div className={s.foot}>
            <p className={s.footNote}>
              Questions, corrections, or a request about your data: write to{' '}
              <a href={`mailto:${contactEmail}`} className={ui.inlineLink}>
                {contactEmail}
              </a>{' '}
              and a person will answer.
            </p>
            <Link to={siblingTo} className={ui.textLink}>
              {siblingLabel}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
